import PropTypes from 'prop-types';
import React from 'react';

const COMPANY = {
    name: 'Nuway Medsolutions Private Limited',
    address: 'C-131, Ground Floor, Noida Sector-2, Noida',
    mobile: '9560728666',
    license20: 'RLF21UP2024001753',
    license21: 'RLF21UP2024001746',
    gstin: '09AAJCN8879L1ZC',
    pan: 'AAJCN8879L',
};

/** Files in public/invoice/ */
const INVOICE_ASSETS = {
    authorizedSignature: `${process.env.PUBLIC_URL || ''}/invoice/sign.png`,
    boardSecretaryStamp: `${process.env.PUBLIC_URL || ''}/invoice/seal.jpeg`,
};

const formatAmount = (value) => {
    const number = parseFloat(value || 0);
    return Number.isNaN(number) ? '0.00' : number.toFixed(2);
};

const formatDate = (value) => {
    const date = value ? new Date(value) : new Date();
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString();
};

const getUser = (sourceData) => sourceData?.mapUserPatient?.User || {};
const getPatient = (sourceData) => sourceData?.mapUserPatient?.Patient || {};

const getLineItems = (paymentItem) => {
    const details = paymentItem?.additionalDetails || {};
    const lineItems = details.lineItems || details.items || details.invoiceItems;

    if (Array.isArray(lineItems) && lineItems.length > 0) {
        return lineItems.map((item, index) => ({
            description: item.description || item.name || item.itemName || item.serviceName || `${paymentItem.invoiceType || 'Service'} Charge`,
            quantity: item.quantity || item.qty || 1,
            rate: item.rate || item.price || item.amount || item.totalAmount || paymentItem.subTotal || paymentItem.totalAmount || 0,
            discount: item.discount || 0,
            gstPercentage: item.gstPercentage ?? paymentItem.gstPercentage ?? 0,
            amount: item.amount || item.totalAmount || paymentItem.totalAmount || 0,
            key: item.id || index,
        }));
    }

    return [{
        description: paymentItem?.notes || `${paymentItem?.invoiceType || 'Service'} Charge`,
        quantity: 1,
        rate: paymentItem?.subTotal || paymentItem?.totalAmount || 0,
        discount: paymentItem?.discount || 0,
        gstPercentage: paymentItem?.gstPercentage || 0,
        amount: paymentItem?.totalAmount || 0,
        key: paymentItem?.id || 'payment-item',
    }];
};

const ServiceDeliveryInvoice = React.forwardRef(({ paymentItem, sourceData, compact = false }, ref) => {
    const user = getUser(sourceData);
    const patient = getPatient(sourceData);
    const lineItems = getLineItems(paymentItem);
    const gstSerial = paymentItem?.gstSerial;
    const billNumber = gstSerial || paymentItem?.additionalDetails?.invoiceNumber || paymentItem?.additionalDetails?.billNumber || `PI-${paymentItem?.id || '-'}`;
    const subTotal = parseFloat(paymentItem?.subTotal || 0);
    const discount = parseFloat(paymentItem?.discount || 0);
    const cgst = parseFloat(paymentItem?.cgst || 0);
    const sgst = parseFloat(paymentItem?.sgst || 0);
    const igst = parseFloat(paymentItem?.igst || 0);
    const gst = parseFloat(paymentItem?.gst || 0);
    const cess = parseFloat(paymentItem?.cessAmount || 0);
    const total = parseFloat(paymentItem?.totalAmount || 0);

    return (
        <div
            ref={ref}
            className={compact ? 'service-delivery-invoice service-delivery-invoice--compact' : 'service-delivery-invoice'}
            style={{
                width: compact ? '100%' : '1000px',
                maxWidth: compact ? 'none' : '1000px',
                boxSizing: 'border-box',
                background: '#ffffff',
                color: '#000000',
                fontFamily: 'Arial, sans-serif',
                fontSize: compact ? '11px' : '12px',
            }}
        >
            <div style={{ border: '1px solid #000', padding: compact ? '6px 8px' : '8px 10px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 230px', gap: '10px', borderBottom: '1px solid #000', paddingBottom: compact ? '4px' : '8px' }}>
                    <div>
                        <div style={{ fontSize: compact ? '20px' : '28px', fontWeight: 800, lineHeight: 1 }}>{COMPANY.name}</div>
                        <div style={{ marginTop: '6px' }}>{COMPANY.address}</div>
                        <div>M. {COMPANY.mobile}</div>
                        <div style={{ display: 'flex', gap: '24px', marginTop: '4px' }}>
                            <span>LICENSE 20<br /><strong>{COMPANY.license20}</strong></span>
                            <span>LICENSE 21<br /><strong>{COMPANY.license21}</strong></span>
                            <span>GSTIN <strong>{COMPANY.gstin}</strong></span>
                            <span>PAN <strong>{COMPANY.pan}</strong></span>
                        </div>
                    </div>
                    <div style={{ borderLeft: '1px solid #000', paddingLeft: '10px' }}>
                        <div style={{ fontSize: compact ? '18px' : '22px', fontWeight: 800 }}>Invoice</div>
                        <div><strong>Bill No:</strong> {billNumber}</div>
                        <div><strong>Payment:</strong> {paymentItem?.status || '-'}</div>
                        <div>{formatDate(paymentItem?.updatedAt || paymentItem?.createdAt)}</div>
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', padding: '6px 0', borderBottom: '1px solid #000', fontWeight: 700 }}>
                    <div>PATIENT NAME <span style={{ fontWeight: 400 }}>{patient.name || user.name || '-'}</span></div>
                    <div>MOBILE <span style={{ fontWeight: 400 }}>{user.contact_no || user.phone || '-'}</span></div>
                    <div>DOCTOR <span style={{ fontWeight: 400 }}></span></div>
                </div>

                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '6px' }}>
                    <thead>
                        <tr style={{ borderBottom: '1px solid #000', textAlign: 'left' }}>
                            <th style={{ padding: '4px' }}>Sr.</th>
                            <th style={{ padding: '4px' }}>Description</th>
                            <th style={{ padding: '4px', textAlign: 'right' }}>Qty.</th>
                            <th style={{ padding: '4px', textAlign: 'right' }}>Rate</th>
                            <th style={{ padding: '4px', textAlign: 'right' }}>Disc.</th>
                            <th style={{ padding: '4px', textAlign: 'right' }}>GST</th>
                            <th style={{ padding: '4px', textAlign: 'right' }}>Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {lineItems.map((item, index) => (
                            <tr key={item.key} style={{ borderBottom: '1px dashed #cccccc' }}>
                                <td style={{ padding: '5px 4px' }}>{index + 1}</td>
                                <td style={{ padding: '5px 4px', fontWeight: 700, color: '#0f5f8f' }}>{item.description}</td>
                                <td style={{ padding: '5px 4px', textAlign: 'right' }}>{item.quantity}</td>
                                <td style={{ padding: '5px 4px', textAlign: 'right' }}>{formatAmount(item.rate)}</td>
                                <td style={{ padding: '5px 4px', textAlign: 'right' }}>{formatAmount(item.discount)}</td>
                                <td style={{ padding: '5px 4px', textAlign: 'right' }}>{item.gstPercentage || 0}%</td>
                                <td style={{ padding: '5px 4px', textAlign: 'right' }}>{formatAmount(item.amount)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div
                    style={{
                        minHeight: compact ? '36px' : '280px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0.16,
                        fontSize: compact ? '36px' : '90px',
                        fontWeight: 800,
                    }}
                >
                    N
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr 1fr', gap: compact ? '8px' : '12px', borderTop: '1px solid #000', paddingTop: compact ? '4px' : '8px' }}>
                    <div>
                        <div style={{ fontWeight: 800, fontSize: '16px' }}>Terms & Conditions</div>
                        <div>-</div>
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px', marginTop: compact ? '4px' : '12px' }}>
                            <img
                                src={INVOICE_ASSETS.authorizedSignature}
                                alt="Authorized signature"
                                crossOrigin="anonymous"
                                style={{ height: compact ? '36px' : '48px', width: 'auto', objectFit: 'contain' }}
                            />
                            <img
                                src={INVOICE_ASSETS.boardSecretaryStamp}
                                alt="For the Board of Directors — Secretary"
                                crossOrigin="anonymous"
                                style={{ height: compact ? '40px' : '56px', width: 'auto', objectFit: 'contain' }}
                            />
                        </div>
                    </div>
                    <div style={{ borderLeft: '1px solid #000', paddingLeft: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>CGST</span><strong>{formatAmount(cgst)}</strong></div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>SGST</span><strong>{formatAmount(sgst)}</strong></div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>IGST</span><strong>{formatAmount(igst)}</strong></div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Total GST</span><strong>{formatAmount(gst)}</strong></div>
                    </div>
                    <div style={{ borderLeft: '1px solid #000', paddingLeft: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Subtotal</span><strong>{formatAmount(subTotal)}</strong></div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Discount</span><strong>{formatAmount(discount)}</strong></div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Cess</span><strong>{formatAmount(cess)}</strong></div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', fontSize: compact ? '16px' : '20px' }}><span>Net</span><strong>Rs. {formatAmount(total)}</strong></div>
                    </div>
                </div>
            </div>
        </div>
    );
});

ServiceDeliveryInvoice.displayName = 'ServiceDeliveryInvoice';

ServiceDeliveryInvoice.propTypes = {
    paymentItem: PropTypes.object,
    sourceData: PropTypes.object,
    compact: PropTypes.bool,
};

export default ServiceDeliveryInvoice;
